import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Paper,
  Collapse,
} from '@mui/material';
import { MarketingAnalytics } from '../utils/analytics';
import marketingTheme from '../themes/marketingTheme';
import { Link, useNavigate } from 'react-router-dom';
import {
  Security,
  AccountTree,
  Route,
  Psychology,
  NotificationsActive,
  Settings,
  PeopleAlt,
} from '@mui/icons-material';
import { 
  FaShieldAlt, 
  FaClock, 
  FaHardHat, 
  FaSync, 
  FaIndustry,
  FaUserShield,
  FaStopwatch
} from 'react-icons/fa';
import { 
  MdSecurity, 
  MdAccessTime, 
  MdConstruction, 
  MdAutorenew, 
  MdPrecisionManufacturing 
} from 'react-icons/md';
import { 
  GiCrane, 
  GiFactory 
} from 'react-icons/gi';
import { 
  RiTimeLine 
} from 'react-icons/ri';
import { 
  HiOutlineShieldCheck 
} from 'react-icons/hi';
import { 
  IoMdTime, 
  IoMdConstruct 
} from 'react-icons/io';
import { isFeatureEnabled } from '../utils/FeatureFlags';
import { SmartCTA, SmartChip } from '../components/SmartCTAs';
import '../components/ScrollAnimations.css';

const Home = () => {
  const navigate = useNavigate();
  const [calendarVisible, setCalendarVisible] = useState(false);
  
  // Scroll animation logic
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add show classes to the main element
          entry.target.classList.add('scroll-show');
          entry.target.classList.add('scroll-show-right');
          entry.target.classList.add('scroll-show-bottom');
          
          // Handle child elements for grid animations and spotlight content
          const childElements = entry.target.querySelectorAll('.scroll-hidden, .scroll-hidden-right, .scroll-hidden-bottom');
          childElements.forEach((child) => {
            child.classList.add('scroll-show');
            child.classList.add('scroll-show-right');
            child.classList.add('scroll-show-bottom');
          });
          
          // Handle spotlight content specifically
          const spotlightContent = entry.target.querySelectorAll('.spotlight-content .scroll-hidden');
          spotlightContent.forEach((child) => {
            child.classList.add('scroll-show');
          });
        }
      });
    }, {
      rootMargin: '0px 0px -30px 0px', // Reduced for faster triggering
      threshold: 0.1
    });

    // Observe both scroll-animate elements and spotlight triggers
    const animatedElements = document.querySelectorAll('.scroll-animate, .spotlight-trigger');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Analytics tracking function
  const handleCellClick = (url) => {
    console.log('handleCellClick', url);
    MarketingAnalytics.productGridClick(url);
    // Navigate to the product page
    window.location.href = url;
  };

  const toggleInlineCalendar = () => {
    console.log("toggling calendar");
    setCalendarVisible(!calendarVisible);
    MarketingAnalytics.consultationBooking();
  };

  useEffect(() => {
    // Scroll animation observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-show');
        }
      });
    }, {
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    });

    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach((el) => observer.observe(el));

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    // Calendly event listeners
    const listenerCB = (event) => {
      console.log(event.data);
      if (event.data.event === 'calendly.date_and_time_selected') {
        console.log('Date and time selected!');
        console.log('calendlyDateAndTimeSelected');
        if (window.gtag) {
          window.gtag('event', 'calendlyDateAndTimeSelected', { event_category: 'engagement' });
        }
      } else if (event.data.event === 'calendly.event_scheduled') {
        console.log('Event scheduled!');
        console.log('calendlyEventScheduled');
        if (window.gtag) {
          window.gtag('event', 'calendlyEventScheduled', { event_category: 'engagement' });
        }
        if (window.lintrk) {
          window.lintrk('track', { conversion_id: 6196196 });
        }
      }
    };

    window.addEventListener('message', listenerCB, false);

    return () => {
      // Cleanup
      observer.disconnect();
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      window.removeEventListener('message', listenerCB, false);
    };
  }, []);
    return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 10 }}>
      {/* Hero Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(36, 36, 42, 0.9) 0%, rgba(45, 45, 51, 0.9) 100%)',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'white',
                }}
              >
                MotionLogic®
                <br />
                <Box
                  component="span"
                  sx={{ color: marketingTheme.palette.primary.main }}
                >
                  Vision AI for Industrial Operations
                </Box>
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  fontSize: '1.3rem',
                  mb: 2,
                  color: marketingTheme.palette.primary.main,
                  fontWeight: 400,
                }}
              >
                {/* Deeper insights across industrial workflows */}
                Improve Productivity and Improve Safety
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.2rem',
                  mb: 4,
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1.6,
                }}
              >
                In industrial workplaces, process inefficiencies and safety risks are often invisible. 
                MotionLogic transforms video data into actionable intelligence.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <SmartCTA
                  context="experience-demo"
                  fallbackHref="/how-it-works"
                  fallbackText="How It Works"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: marketingTheme.palette.primary.main,
                    color: '#000',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: marketingTheme.palette.primary.dark,
                    },
                  }}
                />
                <Button
                  component={Link}
                  to="/product"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: marketingTheme.palette.primary.main,
                    color: marketingTheme.palette.primary.main,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: marketingTheme.palette.primary.dark,
                      color: marketingTheme.palette.primary.dark,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Explore Platform
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                }}
              >
                {/* TODO: Add 3D spatial computing visualization here - animated SVG or GIF showing 
                     industrial space with real-time data overlays, worker movement patterns, 
                     and spatial intelligence indicators */}
                <Box
                  component={Link}
                  to="/product/overview"
                  sx={{ textDecoration: 'none', width: '100%', textAlign: 'center' }}
                >
                  <img
                    src="/images/process_segmentation.png"
                    alt="MotionLogic Spatial Computing Platform"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* Spotlight Section */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 8, 
          mt: 4
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'transparent',
            p: { xs: 2, md: 4 },
            borderRadius: '8px',
          }}
        >
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                textAlign: 'center',
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  borderRadius: '8px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover:after': {
                  opacity: 1
                }
              }}>
                <img
                  src="/images/machine_learning_vision.png"
                  alt="AI Vision"
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto', 
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                  position: 'relative',
                  paddingBottom: '12px',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '60px',
                    height: '3px',
                    backgroundColor: marketingTheme.palette.primary.main,
                    borderRadius: '2px',
                  }
                }}
              >
                Vision AI for Industrial Productivity & Safety
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  mb: 4,
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1.7,
                }}
              >
                Our spatial computing platform MotionLogic® increases awareness over production events.
                MotionLogic's Vision AI solutions enables production manager to extract rich, fine-grained 
                information from video feeds, opening up a world of possibilities for process optimisation.
              </Typography>

              <Box sx={{ 
                mb: 4, 
                pl: 2, 
                borderLeft: '3px solid rgba(237, 185, 46, 0.5)',
                bgcolor: 'rgba(237, 185, 46, 0.05)',
                py: 2,
                px: 2,
                borderRadius: '0 4px 4px 0'
              }}>
                <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
                  • Visually infer sources of time waste and value-add time
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  • Visually infer near misses and safety critical events
                </Typography>
              </Box>

              <Button
                component={Link}
                to="/get-started"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: marketingTheme.palette.primary.dark,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Begin Assessment
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Product Icons Grid */}
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            color: 'white',
            fontWeight: 600,
            mt: 10,
            mb: 5,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: '-15px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '3px',
              backgroundColor: marketingTheme.palette.primary.main,
              borderRadius: '2px',
            }
          }}
        >
          {/* MotionLogic® Industrial Solutions */}
          MotionLogic® Insights
        </Typography>
        
        <Box 
          sx={{ 
            backgroundColor: 'rgba(45, 45, 51, 0.3)', 
            borderRadius: '12px', 
            p: { xs: 2, md: 4 },
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}
          className="scroll-animate grid-animate"
        >
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} lg={2.4} className="scroll-hidden">
              <Card
                component={Link}
                to="/product/worker-safety"
                onClick={() => handleCellClick('/product/worker-safety')}
                sx={{
                  backgroundColor: 'rgba(45, 45, 51, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    borderColor: 'rgba(237, 185, 46, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80px',
                    width: '80px',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    <FaUserShield size={48} color={marketingTheme.palette.primary.main} />
                  </Box>
                  <Typography sx={{ 
                    // color: marketingTheme.palette.primary.main, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                  }}>
                    Worker Safety
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} lg={2.4} className="scroll-hidden">
              <Card
                component={Link}
                to="/product/ergonomic-analytics"
                onClick={() => handleCellClick('/product/ergonomic-analytics')}
                sx={{
                  backgroundColor: 'rgba(45, 45, 51, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    borderColor: 'rgba(237, 185, 46, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80px',
                    width: '80px',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    <PeopleAlt sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />
                  </Box>
                  <Typography sx={{ 
                    // color: marketingTheme.palette.primary.main, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                  }}>
                    Ergonomic Analysis
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={2.4} className="scroll-hidden">
              <Card
                component={Link}
                to="/product/time-and-motion"
                onClick={() => handleCellClick('/product/time-and-motion')}
                sx={{
                  backgroundColor: 'rgba(45, 45, 51, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    borderColor: 'rgba(237, 185, 46, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80px',
                    width: '80px',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    <FaStopwatch size={48} color={marketingTheme.palette.primary.main} />
                  </Box>
                  <Typography sx={{ 
                    // color: marketingTheme.palette.primary.main, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                  }}>
                    Time-and-Motion
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={2.4} className="scroll-hidden">
              <Card
                component={Link}
                to="/product/lean-waste-analytics"
                onClick={() => handleCellClick('/product/lean-waste-analytics')}
                sx={{
                  backgroundColor: 'rgba(45, 45, 51, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    borderColor: 'rgba(237, 185, 46, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80px',
                    width: '80px',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    <AccountTree sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />
                  </Box>
                  <Typography sx={{ 
                    // color: marketingTheme.palette.primary.main, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                  }}>
                    Lean Waste Analysis
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={2.4} className="scroll-hidden">
              <Card
                component={Link}
                to="/product/spaghetti-analytics"
                onClick={() => handleCellClick('/product/spaghetti-analytics')}
                sx={{
                  backgroundColor: 'rgba(45, 45, 51, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    borderColor: 'rgba(237, 185, 46, 0.3)',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80px',
                    width: '80px',
                    mb: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}>
                    <Route sx={{ fontSize: 48, color: marketingTheme.palette.primary.main }} />
                  </Box>
                  <Typography sx={{ 
                    // color: marketingTheme.palette.primary.main, 
                    fontSize: '1rem', 
                    fontWeight: 'bold',
                    fontFamily: 'Roboto, sans-serif',
                  }}>
                    Spaghetti Analysis
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Pressing Challenges */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(30px)',
          transition: 'all 0.36s ease-out',
          transitionDelay: '0.12s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Paper
          elevation={2}
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: { xs: 3, md: 5 },
          }}
        >
          <Typography
            variant="h2"
            className="scroll-hidden"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 6,
              color: '#ffffff',
              textAlign: 'center',
            }}
          >
            Pressing Challenges
          </Typography>

          <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
          {/* Column 1 - Stagnant Productivity Levels */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box>
              <Typography
                variant="h5"
                className="scroll-hidden"
                sx={{
                  fontSize: '1.3rem',
                  fontWeight: 600,
                  mb: 3,
                  color: '#ffffff',
                  textAlign: 'left',
                }}
              >
                Low Productivity Levels
              </Typography>
              <Typography
                variant="body1"
                className="scroll-hidden"
                sx={{
                  fontSize: '1rem',
                  mb: 4,
                  color: '#ffffff',
                  lineHeight: 1.6,
                  textAlign: 'left',
                }}
              >
                Operations teams struggle to identify why productivity gains have plateaued despite investment. Manual procedures harbor hidden inefficiencies that cost <strong>millions annually per site</strong> without clear visibility.
                {/* Operations teams struggle to identify why productivity gains have plateaued despite investment. Manual procedures harbor hidden inefficiencies that can cost <strong>$25 million per site × 10 sites = $250 million across the entire portfolio</strong>, with no clear line of sight. */}
              </Typography>
              </Box>
              <Box
                className="scroll-hidden"
                sx={{
                  width: '100%',
                  height: 200,
                  borderRadius: 2,
                  overflow: 'hidden',
                  mt: 2,
                }}
              >
                <img
                  src="/images/productivity.jpg"
                  alt="Productivity in manufacturing"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Column 2 - Time & Cost Overruns */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography
                  variant="h5"
                  className="scroll-hidden"
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    mb: 3,
                    color: '#ffffff',
                    textAlign: 'left',
                  }}
                >
                  Time & Cost Overruns
                </Typography>
                <Typography
                  variant="body1"
                  className="scroll-hidden"
                  sx={{
                    fontSize: '1rem',
                    mb: 4,
                    color: '#ffffff',
                    lineHeight: 1.6,
                    textAlign: 'left',
                  }}
                >
                  Heavy manufacturing projects consistently exceed timelines due to unoptimized workflows. Process delays compound throughout operations, with up to <strong>80% being avoidable</strong> through better analysis.
                </Typography>
              </Box>
              <Box
                className="scroll-hidden"
                sx={{
                  width: '100%',
                  height: 200,
                  borderRadius: 2,
                  overflow: 'hidden',
                  mt: 2,
                }}
              >
                <img
                  src="/images/welding_production.jpg"
                  alt="Welding and production operations"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Column 3 - Safety Issues */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              textAlign: 'center', 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography
                  variant="h5"
                  className="scroll-hidden"
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    mb: 3,
                    color: '#ffffff',
                    textAlign: 'left',
                  }}
                >
                  Safety Issues
                </Typography>
                <Typography
                  variant="body1"
                  className="scroll-hidden"
                  sx={{
                    fontSize: '1rem',
                    mb: 4,
                    color: '#ffffff',
                    lineHeight: 1.6,
                    textAlign: 'left',
                  }}
                >
                  Workers compensation claims spike when repetitive tasks cause cumulative injuries over time. Insurance premiums increase dramatically, with <strong>100%+ rate hikes</strong> after incident patterns emerge.
                </Typography>
              </Box>
              <Box
                className="scroll-hidden"
                sx={{
                  width: '100%',
                  height: 200,
                  borderRadius: 2,
                  overflow: 'hidden',
                  mt: 2,
                }}
              >
                <img
                  src="/images/safety.jpg"
                  alt="Manufacturing safety protocols"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        </Paper>
      </Container>

      {/* Better Work Design Section */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(30px)',
          transition: 'all 0.48s ease-out',
          transitionDelay: '0.12s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Paper
          elevation={2}
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: { xs: 3, md: 5 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 2,
              color: 'white',
            }}
          >
            Better Work Design
          </Typography>
          
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              fontWeight: 500,
              mb: 3,
              color: marketingTheme.palette.primary.main,
            }}
          >
            Real-Time Insights for Safety and Productivity
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              mb: 4,
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
            }}
          >
            Waiting and excess motion are measured sparsely through job cards, resulting in a lack of hard-data.
            This lack of data means that inefficiencies can be elusive. Safety managers and production managers are operating without all the facts.
            Poor metrics on waste cause a substantial loss of revenue and increase in production costs.
            <br /><br />
            MotionLogic® provides a new way to measure waste and process inefficiency. Delivering a safety improvement and a margin enhancement.
          </Typography>

          {/* Key Metrics Section */}
          <Container maxWidth="lg" sx={{ py: 6 }}>
            <Grid container spacing={4} justifyContent="center" className="grid-animate">
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', position: 'relative', py: 3 }}>
                  {/* Sleek dot pattern background */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120px',
                      height: '120px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '8px 8px',
                      borderRadius: '50%',
                      zIndex: 0,
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          up
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          to
                        </Typography>
                      </Box>
                      <Typography variant="h2" sx={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>
                        25%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                        Improvement in maintenance staff productivity
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1dd1a1', fontSize: '1.5rem' }}>
                        ↑
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', position: 'relative', py: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120px',
                      height: '120px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '8px 8px',
                      borderRadius: '50%',
                      zIndex: 0,
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          up
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          to
                        </Typography>
                      </Box>
                      <Typography variant="h2" sx={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>
                        30%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                        Reduction in maintenance costs
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1dd1a1', fontSize: '1.5rem' }}>
                        ↓
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', position: 'relative', py: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120px',
                      height: '120px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '8px 8px',
                      borderRadius: '50%',
                      zIndex: 0,
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          up
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          to
                        </Typography>
                      </Box>
                      <Typography variant="h2" sx={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>
                        40%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                        Reduction in manual handling incidents
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1dd1a1', fontSize: '1.5rem' }}>
                        ↓
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', position: 'relative', py: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120px',
                      height: '120px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
                      backgroundSize: '8px 8px',
                      borderRadius: '50%',
                      zIndex: 0,
                    }}
                  />
                  <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          up
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1 }}>
                          to
                        </Typography>
                      </Box>
                      <Typography variant="h2" sx={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold' }}>
                        20%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                        Increase in production throughput
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1dd1a1', fontSize: '1.5rem' }}>
                        ↑
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Paper>
      </Container>

      {/* Platform Study Types */}
      <Box 
        sx={{ 
          backgroundColor: 'rgba(45, 45, 51, 0.5)', 
          py: 6, 
          borderRadius: '8px',
          mt: 4,
          opacity: 0,
          filter: 'blur(4px)',
          transform: 'translateY(30px)',
          transition: 'all 0.48s ease-out',
          transitionDelay: '0.18s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              color: 'white',
              fontWeight: 600,
              mb: 5,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: '-15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80px',
                height: '3px',
                backgroundColor: marketingTheme.palette.primary.main,
                borderRadius: '2px',
              }
            }}
          >
            Core Study Types
          </Typography>
          <Grid container spacing={5} justifyContent="center" className="grid-animate">
            <Grid item xs={12} md={5} onClick={() => handleCellClick('/product/time-and-motion')} className="scroll-hidden">
              <Paper
                elevation={4}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(237, 185, 46, 0.3)',
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    backgroundColor: marketingTheme.palette.primary.main,
                  }
                }}
              >
                <Typography variant="h5" sx={{ color: marketingTheme.palette.primary.main, fontWeight: 600, mb: 3 }}>
                  Time-and-Motion Studies
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.7 }}>
                  Automated analysis of worker movements, tool usage, and process efficiency to eliminate waste and optimize workflows.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5} onClick={() => handleCellClick('/product/ergonomic-analytics')} className="scroll-hidden">
              <Paper
                elevation={4}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(237, 185, 46, 0.3)',
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    backgroundColor: marketingTheme.palette.primary.main,
                  }
                }}
              >
                <Typography variant="h5" sx={{ color: marketingTheme.palette.primary.main, fontWeight: 600, mb: 3 }}>
                  Ergonomic Studies
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.7 }}>
                  Real-time assessment of postures, movements, and safety risks to proactively prevent workplace injuries.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose MotionLogic® Spotlight */}
      <Box 
        sx={{ 
          backgroundColor: 'rgba(237, 185, 46, 0.05)', 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(50px)',
          transition: 'all 0.48s ease-out',
          transitionDelay: '0.18s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'white',
                }}
              >
                Why Choose MotionLogic®?
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem',
                  mb: 4,
                  color: marketingTheme.palette.primary.main,
                  fontWeight: 500,
                }}
              >
                See how our enterprise customers are transforming operations
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Grid container spacing={3} className="grid-animate">
                  <Grid item xs={12} sm={6} className="scroll-hidden">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FaShieldAlt size={24} color={marketingTheme.palette.primary.main} style={{ marginRight: '12px' }} />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                        Manage safety proactively
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 4.5 }}>
                      Identify and mitigate risks before they become incidents
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} className="scroll-hidden">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FaClock size={24} color={marketingTheme.palette.primary.main} style={{ marginRight: '12px' }} />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                        See where time actually goes
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 4.5 }}>
                      See where time is lost and where time can be gained
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} className="scroll-hidden">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FaIndustry size={24} color={marketingTheme.palette.primary.main} style={{ marginRight: '12px' }} />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                        Make decisions with data, not guesswork
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 4.5 }}>
                      Gain clarity on process flows and person-tool interactions
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} className="scroll-hidden">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FaStopwatch size={24} color={marketingTheme.palette.primary.main} style={{ marginRight: '12px' }} />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                        Get continuous improvement working
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', ml: 4.5 }}>
                      Turn one-off studies into ongoing work intelligence
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <SmartCTA
                  context="experience-demo"
                  fallbackHref="/get-started"
                  fallbackText="Start Your Assessment"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: marketingTheme.palette.primary.main,
                    color: '#000',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: marketingTheme.palette.primary.dark,
                    },
                  }}
                />
                <Button
                  component={Link}
                  to="/how-it-works"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: marketingTheme.palette.primary.main,
                    color: marketingTheme.palette.primary.main,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: marketingTheme.palette.primary.dark,
                      color: marketingTheme.palette.primary.dark,
                      backgroundColor: 'rgba(237, 185, 46, 0.1)',
                    },
                  }}
                >
                  How It Works
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                textAlign: 'center',
                p: 3,
              }}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: '2px solid rgba(237, 185, 46, 0.3)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  }}
                >
                  <iframe
                    src="https://www.youtube.com/embed/1stQbXi217I?autoplay=0&loop=0&mute=0&rel=0&controls=1"
                    title="Why Choose MotionLogic - Enterprise Vision AI"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '10px',
                    }}
                  />
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    mt: 2,
                    fontStyle: 'italic',
                  }}
                >
                  See MotionLogic in action across industrial environments
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Enhanced Awareness Across Operations */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(30px)',
          transition: 'all 0.36s ease-out',
          transitionDelay: '0.12s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Paper
          elevation={2}
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: { xs: 3, md: 5 },
          }}
          className="spotlight-content"
        >
          <Typography
            variant="h2"
            className="scroll-hidden"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 4,
              color: 'white',
            }}
          >
            Enhanced Awareness Across Operations
          </Typography>

          <Typography
            variant="body1"
            className="scroll-hidden"
            sx={{
              fontSize: '1.1rem',
              mb: 4,
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
            }}
          >
            Many workplace risks and inefficiencies remain elusive to traditional observation methods. Ergonomic strain accumulates gradually. Process flow inefficiencies compound over time. Understanding these patterns requires clear comprehension.
            <br /><br />
            MotionLogic® helps you comprehend workflow dynamics with greater clarity:
          </Typography>

          <List sx={{ mb: 4 }} className="scroll-hidden">
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText 
                primary="Subtle indicators of ergonomic strain and fatigue"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.1rem',
                  }
                }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText 
                primary="Elusive workflow bottlenecks impacting productivity"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.1rem',
                  }
                }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText 
                primary="Key safety moments that benefit from closer analysis"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.1rem',
                  }
                }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText 
                primary="Operational patterns that reveal optimisation opportunities"
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.1rem',
                  }
                }}
              />
            </ListItem>
          </List>

          <Box sx={{ textAlign: 'center', mb: 4 }} className="scroll-hidden">
            <img
              src="/images/motionlogic-diagram.svg"
              alt="MotionLogic"
              style={{ 
                maxWidth: '100%', 
                height: 'auto', 
                borderRadius: '8px',
              }}
            />
          </Box>

          <Typography
            variant="body1"
            className="scroll-hidden"
            sx={{
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
            }}
          >
            MotionLogic® Vision AI technology interprets billions of pixels from video feeds, uncovering hidden insights about your production flow.
            <br /><br />
            For EHS and operations leaders, this enhanced awareness enables more informed decisions. Enact operational changes based on empirical evidence, and shift from reactive responses to proactive improvements.
          </Typography>
        </Paper>
      </Container>


      {/* How MotionLogic® Improves Safety Section */}
      <Box 
        sx={{ 
          backgroundColor: 'rgba(45, 45, 51, 0.5)', 
          py: 8,
          opacity: 0,
          filter: 'blur(4px)',
          transform: 'translateY(30px)',
          transition: 'all 0.48s ease-out',
          transitionDelay: '0.12s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 6,
              color: 'white',
              textAlign: 'center',
            }}
          >
            How MotionLogic® Improves Safety
          </Typography>

          <Grid container spacing={4} className="grid-animate">
            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <Security sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  Proactive Indication
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Real-time Audible Alerts Based on Risk Factors
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <PeopleAlt sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  Lumbar Risk
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Mitigating the Potential for Lower Back Injuries During Lifting, Bending, and Twisting
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <Security sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  Forklift Safety
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Minimising Risks and Incidents Involving Forklifts and Similar Powered Industrial Equipment
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <FaHardHat size={80} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  PPE Compliance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Ensuring Adherence to Personal Protective Equipment (PPE) Guidelines in Designated Areas
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <MdAutorenew size={80} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  Repetitive Motion Strain
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Providing Timely Notifications on Repetitive Motion Strain Risk Based on Joint Movements
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4} className="scroll-hidden">
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box
                  component={Link}
                  to="/product/worker-safety"
                  sx={{ 
                    textDecoration: 'none', 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '120px',
                    width: '120px',
                    mb: 2
                  }}
                >
                  <GiCrane size={80} color={marketingTheme.palette.primary.main} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  Crane Safety
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Enhancing Safety Measures for Workers Engaged in Crane Operations or in Proximity to Crane Activities
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Video Section with Enhanced Awareness */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(30px)',
          transition: 'all 0.36s ease-out',
          transitionDelay: '0.06s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Grid container spacing={6} alignItems="center" className="spotlight-trigger">
          <Grid item xs={12} md={6} className="spotlight-content">
            <Box
              className="scroll-hidden"
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
                borderRadius: 2,
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/1stQbXi217I?autoplay=1&loop=1&mute=1&rel=0&playlist=1stQbXi217I"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} className="spotlight-content">
            <Typography
              variant="h2"
              className="scroll-hidden"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 600,
                mb: 2,
                color: 'white',
              }}
            >
              Improve Work Design
            </Typography>
            
            <Typography
              variant="h4"
              className="scroll-hidden"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                fontWeight: 500,
                mb: 3,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Represent the Structure and Events of your Operation
            </Typography>

            <Typography
              variant="body1"
              className="scroll-hidden"
              sx={{
                fontSize: '1.1rem',
                mb: 4,
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: 1.6,
              }}
            >
              Your assembly processes are scheduled for X hours but consistently run 20-30% longer. You see the delays but can't pinpoint where the time goes. Our system shows you exactly where every second is lost - in material handling, tool changes, or worker movement - so you can fix production bottlenecks.
              <br /><br />
              Use vision to automatically analyse physical workspace layout, equipment utilisation patterns, and workflow patterns as they happen.
              <br /><br />
              Get the hard data on what's actually slowing down your production, use this data to improve work design.
            </Typography>

            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              className="scroll-hidden"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  backgroundColor: marketingTheme.palette.primary.dark,
                },
              }}
            >
              Get In Contact
            </Button>
          </Grid>
        </Grid>
      </Container>

      {/* From Invisible to Actionable */}
      <Box 
        sx={{ 
          backgroundColor: 'rgba(45, 45, 51, 0.5)', 
          py: 8,
          opacity: 0,
          filter: 'blur(3px)',
          transform: 'translateY(30px)',
          transition: 'all 0.48s ease-out',
          transitionDelay: '0.24s',
          '&.scroll-show': {
            opacity: 1,
            filter: 'blur(0)',
            transform: 'translateY(0)',
          }
        }}
        className="scroll-animate"
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 6,
              color: 'white',
              textAlign: 'center',
            }}
          >
            From Invisible to Actionable
          </Typography>

          <Grid container spacing={4} className="grid-animate">
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '120px',
                  width: '120px',
                  mb: 2
                }}>
                  <Psychology sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  1. Comprehend
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  MotionLogic® analyses video feeds to detect early warning signs of ergonomic strain, excess motion, process deficiencies, and safety risks
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '120px',
                  width: '120px',
                  mb: 2
                }}>
                  <NotificationsActive sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  2. Alert
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Real-time notifications flag potential issues before they become problems - from repetitive strain to workflow bottlenecks
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '120px',
                  width: '120px',
                  mb: 2
                }}>
                  <Settings sx={{ fontSize: 80, color: marketingTheme.palette.primary.main }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2,
                    color: 'white',
                  }}
                >
                  3. Enact
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: 1.6,
                  }}
                >
                  Data-driven recommendations help you redesign workflows for both productivity and worker wellbeing
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} className="scroll-animate spotlight-trigger">
        <Paper
          elevation={3}
          sx={{
            backgroundColor: '#2d2d33',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: { xs: 3, md: 5 },
            textAlign: 'center',
          }}
          className="spotlight-content"
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 2,
              color: 'white',
            }}
            className="scroll-hidden"
          >
            Vision AI for Productivity and Safety
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: '1.2rem',
              mb: 4,
              color: 'rgba(255, 255, 255, 0.8)',
            }}
            className="scroll-hidden"
          >
            We enhance <strong>productivity</strong> and <strong>safety</strong> through intelligent vision solutions.
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              mb: 3,
              color: 'white',
              fontWeight: 500,
            }}
            className="scroll-hidden"
          >
            Proud Member of:
          </Typography>

          <Box sx={{ mb: 4 }} className="scroll-hidden">
            <img
              src="/images/HN_LOGO.png"
              alt="Member Logo"
              style={{ maxWidth: 400, width: '100%', height: 'auto' }}
            />
          </Box>

          <Grid container spacing={3} justifyContent="center" className="grid-animate">
            <Grid item xs={12} md={4} className="scroll-hidden">
              <SmartCTA
                context="experience-demo"
                fallbackHref="/contact"
                fallbackText="See MotionLogic® in Action"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontSize: '1.1rem',
                  py: 1.5,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: marketingTheme.palette.primary.dark,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} className="scroll-hidden">
              <SmartCTA
                context="roi-calculation"
                fallbackHref="/contact"
                fallbackText="Send a message"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontSize: '1.1rem',
                  py: 1.5,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: marketingTheme.palette.primary.dark,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} className="scroll-hidden">
              <Button
                component={Link}
                to="/product"
                variant="outlined"
                size="large"
                fullWidth
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontSize: '1.1rem',
                  py: 1.5,
                  '&:hover': {
                    borderColor: marketingTheme.palette.primary.main,
                    backgroundColor: 'rgba(237, 185, 46, 0.1)',
                  },
                }}
              >
                Learn More
              </Button>
            </Grid>
          </Grid>

          {/* Calendly Calendar Widget */}
          <Collapse in={calendarVisible} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
                Book your free initial consult:
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  minHeight: '630px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/alex-mcclung/45-minute-meeting?hide_event_type_details=1&background_color=24242a&text_color=ffffff"
                  style={{ minWidth: '320px', height: '630px', width: '100%' }}
                />
              </Box>
            </Box>
          </Collapse>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;
